/**
 * Opening Book for ChessDrez AI
 *
 * Provides level-aware opening moves so the AI plays realistic,
 * recognizable openings instead of cold Stockfish engine moves.
 *
 * Level mapping (approximate ELO):
 *   1-4   → 600-900   Beginner  (random/weak book)
 *   5-8   → 1000-1300 Novice    (common but not always best)
 *   9-13  → 1400-1700 Club      (mainlines)
 *   14-17 → 1800-2100 Advanced  (deep mainlines + sidelines)
 *   18-20 → 2200+     Master    (full theory)
 */
import { Chess } from 'chess.js';
// Key: FEN position string (first 2 fields: pieces + turn only)
const OPENING_BOOK = new Map();
// ─── Helper ─────────────────────────────────────────────────────────────────────
function fenKey(fen) {
    // Use pieces + side-to-move only (ignore castling, en-passant, clocks)
    const parts = fen.split(' ');
    return `${parts[0]} ${parts[1]}`;
}
function addPosition(fen, entries) {
    OPENING_BOOK.set(fenKey(fen), entries);
}
// ─── Starting Position ──────────────────────────────────────────────────────────
addPosition('rnbqkbnr/pppppppp/8/8/8/8/PPPPPPPP/RNBQKBNR w KQkq - 0 1', [
    // Beginners play random pawn moves
    { moves: ['e2e3'], minLevel: 1, maxLevel: 3, weight: 5 },
    { moves: ['d2d3'], minLevel: 1, maxLevel: 3, weight: 5 },
    { moves: ['h2h3'], minLevel: 1, maxLevel: 2, weight: 3 },
    { moves: ['a2a3'], minLevel: 1, maxLevel: 2, weight: 3 },
    { moves: ['g1f3'], minLevel: 1, maxLevel: 2, weight: 4 },
    // Common moves for all levels
    { moves: ['e2e4'], minLevel: 1, maxLevel: 20, weight: 40 },
    { moves: ['d2d4'], minLevel: 3, maxLevel: 20, weight: 30 },
    { moves: ['c2c4'], minLevel: 9, maxLevel: 20, weight: 15 },
    { moves: ['g1f3'], minLevel: 9, maxLevel: 20, weight: 15 },
    { moves: ['b1c3'], minLevel: 12, maxLevel: 20, weight: 5 },
    { moves: ['f2f4'], minLevel: 7, maxLevel: 20, weight: 5 },
]);
// ─── After 1.e4 ────────────────────────────────────────────────────────────────
addPosition('rnbqkbnr/pppppppp/8/8/4P3/8/PPPP1PPP/RNBQKBNR b KQkq - 0 1', [
    // Beginner random responses
    { moves: ['h7h6'], minLevel: 1, maxLevel: 3, weight: 4 },
    { moves: ['a7a6'], minLevel: 1, maxLevel: 3, weight: 4 },
    { moves: ['g8f6'], minLevel: 1, maxLevel: 4, weight: 8 },
    // Common defenses
    { moves: ['e7e5'], minLevel: 1, maxLevel: 20, weight: 35 }, // Open game
    { moves: ['c7c5'], minLevel: 4, maxLevel: 20, weight: 30 }, // Sicilian
    { moves: ['e7e6'], minLevel: 5, maxLevel: 20, weight: 15 }, // French
    { moves: ['c7c6'], minLevel: 7, maxLevel: 20, weight: 12 }, // Caro-Kann
    { moves: ['d7d5'], minLevel: 9, maxLevel: 20, weight: 8 }, // Scandinavian
    { moves: ['g8f6'], minLevel: 10, maxLevel: 20, weight: 8 }, // Alekhine
]);
// ─── After 1.d4 ────────────────────────────────────────────────────────────────
addPosition('rnbqkbnr/pppppppp/8/8/3P4/8/PPP1PPPP/RNBQKBNR b KQkq - 0 1', [
    { moves: ['h7h6'], minLevel: 1, maxLevel: 3, weight: 4 },
    { moves: ['a7a6'], minLevel: 1, maxLevel: 3, weight: 4 },
    { moves: ['d7d5'], minLevel: 1, maxLevel: 20, weight: 30 },
    { moves: ['g8f6'], minLevel: 3, maxLevel: 20, weight: 35 },
    { moves: ['e7e6'], minLevel: 4, maxLevel: 20, weight: 15 },
    { moves: ['c7c5'], minLevel: 6, maxLevel: 20, weight: 10 },
    { moves: ['f7f5'], minLevel: 8, maxLevel: 20, weight: 5 },
    { moves: ['b8c6'], minLevel: 6, maxLevel: 20, weight: 5 },
]);
// ─── 1.c4 (English) ────────────────────────────────────────────────────────────
addPosition('rnbqkbnr/pppppppp/8/8/2P5/8/PP1PPPPP/RNBQKBNR b KQkq - 0 1', [
    { moves: ['e7e5'], minLevel: 1, maxLevel: 20, weight: 30 },
    { moves: ['c7c5'], minLevel: 5, maxLevel: 20, weight: 25 },
    { moves: ['g8f6'], minLevel: 5, maxLevel: 20, weight: 25 },
    { moves: ['e7e6'], minLevel: 6, maxLevel: 20, weight: 15 },
    { moves: ['c7c6'], minLevel: 8, maxLevel: 20, weight: 5 },
]);
// ─── 1.Nf3 (Réti) ──────────────────────────────────────────────────────────────
addPosition('rnbqkbnr/pppppppp/8/8/8/5N2/PPPPPPPP/RNBQKB1R b KQkq - 0 1', [
    { moves: ['d7d5'], minLevel: 1, maxLevel: 20, weight: 30 },
    { moves: ['g8f6'], minLevel: 5, maxLevel: 20, weight: 25 },
    { moves: ['c7c5'], minLevel: 5, maxLevel: 20, weight: 20 },
    { moves: ['e7e6'], minLevel: 5, maxLevel: 20, weight: 15 },
]);
// ─── OPEN GAME (1.e4 e5) ───────────────────────────────────────────────────────
addPosition('rnbqkbnr/pppp1ppp/8/4p3/4P3/8/PPPP1PPP/RNBQKBNR w KQkq - 0 2', [
    { moves: ['d1h5'], minLevel: 1, maxLevel: 4, weight: 10 }, // Scholar's mate attempt
    { moves: ['f1c4'], minLevel: 1, maxLevel: 6, weight: 15 }, // Italian early
    { moves: ['d2d4'], minLevel: 1, maxLevel: 6, weight: 10 }, // Center game
    { moves: ['g1f3'], minLevel: 3, maxLevel: 20, weight: 70 }, // Best move
    { moves: ['f2f4'], minLevel: 6, maxLevel: 16, weight: 10 }, // King's Gambit
    { moves: ['b1c3'], minLevel: 5, maxLevel: 15, weight: 5 },
]);
// ─── 1.e4 e5 2.Nf3 ─────────────────────────────────────────────────────────────
addPosition('rnbqkbnr/pppp1ppp/8/4p3/4P3/5N2/PPPP1PPP/RNBQKB1R b KQkq - 0 2', [
    { moves: ['b8c6'], minLevel: 1, maxLevel: 20, weight: 50 }, // Natural and best
    { moves: ['d7d6'], minLevel: 1, maxLevel: 20, weight: 20 }, // Philidor
    { moves: ['g8f6'], minLevel: 5, maxLevel: 20, weight: 20 }, // Petroff
    { moves: ['f7f5'], minLevel: 1, maxLevel: 6, weight: 10 }, // Latvian Gambit
]);
// ─── 1.e4 e5 2.Nf3 Nc6 ─────────────────────────────────────────────────────────
addPosition('r1bqkbnr/pppp1ppp/2n5/4p3/4P3/5N2/PPPP1PPP/RNBQKB1R w KQkq - 0 3', [
    { moves: ['f1b5'], minLevel: 6, maxLevel: 20, weight: 40 }, // Spanish
    { moves: ['f1c4'], minLevel: 3, maxLevel: 20, weight: 35 }, // Italian
    { moves: ['d2d4'], minLevel: 5, maxLevel: 18, weight: 15 }, // Scotch
    { moves: ['b1c3'], minLevel: 3, maxLevel: 12, weight: 10 }, // Three Knights
    { moves: ['f2f4'], minLevel: 5, maxLevel: 14, weight: 5 }, // King's Gambit style
]);
// ─── SPANISH (1.e4 e5 2.Nf3 Nc6 3.Bb5) ─────────────────────────────────────────
addPosition('r1bqkbnr/pppp1ppp/2n5/1B2p3/4P3/5N2/PPPP1PPP/RNBQK2R b KQkq - 0 3', [
    { moves: ['a7a6'], minLevel: 5, maxLevel: 20, weight: 45 }, // Morphy Defense (main)
    { moves: ['g8f6'], minLevel: 6, maxLevel: 20, weight: 30 }, // Berlin
    { moves: ['f7f5'], minLevel: 1, maxLevel: 8, weight: 8 },
    { moves: ['d7d6'], minLevel: 3, maxLevel: 20, weight: 12 }, // Steinitz
    { moves: ['b8c6'], minLevel: 1, maxLevel: 5, weight: 5 },
]);
// ─── SPANISH — 3...a6 4.Ba4 ────────────────────────────────────────────────────
addPosition('r1bqkbnr/1ppp1ppp/p1n5/4p3/B3P3/5N2/PPPP1PPP/RNBQK2R b KQkq - 0 4', [
    { moves: ['g8f6'], minLevel: 5, maxLevel: 20, weight: 50 }, // Main mainline
    { moves: ['d7d6'], minLevel: 3, maxLevel: 16, weight: 20 },
    { moves: ['f8c5'], minLevel: 5, maxLevel: 18, weight: 20 }, // Classical
    { moves: ['b7b5'], minLevel: 7, maxLevel: 20, weight: 10 }, // Quick ...b5
]);
// ─── SPANISH — 3...a6 4.Ba4 Nf6 5.O-O ──────────────────────────────────────────
addPosition('r1bqkb1r/1ppp1ppp/p1n2n2/4p3/B3P3/5N2/PPPP1PPP/RNBQ1RK1 b KQkq - 0 5', [
    { moves: ['f8e7'], minLevel: 7, maxLevel: 20, weight: 40 }, // Closed Spanish
    { moves: ['b7b5'], minLevel: 5, maxLevel: 20, weight: 25 }, // Open Spanish
    { moves: ['f8c5'], minLevel: 5, maxLevel: 18, weight: 20 },
    { moves: ['f6e4'], minLevel: 8, maxLevel: 20, weight: 15 }, // Open Spanish Pawn grab
]);
// ─── BERLIN (1.e4 e5 2.Nf3 Nc6 3.Bb5 Nf6) ─────────────────────────────────────
addPosition('r1bqkb1r/pppp1ppp/2n2n2/1B2p3/4P3/5N2/PPPP1PPP/RNBQK2R w KQkq - 0 4', [
    { moves: ['e1g1'], minLevel: 10, maxLevel: 20, weight: 40 }, // O-O
    { moves: ['d2d3'], minLevel: 6, maxLevel: 20, weight: 30 },
    { moves: ['b5c6'], minLevel: 8, maxLevel: 20, weight: 20 }, // Exchange
    { moves: ['f6e4'], minLevel: 1, maxLevel: 8, weight: 10 },
]);
// ─── ITALIAN (1.e4 e5 2.Nf3 Nc6 3.Bc4) ─────────────────────────────────────────
addPosition('r1bqkbnr/pppp1ppp/2n5/4p3/2B1P3/5N2/PPPP1PPP/RNBQK2R b KQkq - 0 3', [
    { moves: ['f8c5'], minLevel: 3, maxLevel: 20, weight: 40 }, // Giuoco Piano
    { moves: ['g8f6'], minLevel: 5, maxLevel: 20, weight: 30 }, // Two Knights
    { moves: ['f7f5'], minLevel: 1, maxLevel: 6, weight: 10 },
    { moves: ['h7h6'], minLevel: 1, maxLevel: 4, weight: 5 },
    { moves: ['b8c6'], minLevel: 1, maxLevel: 5, weight: 5 },
    { moves: ['d7d6'], minLevel: 3, maxLevel: 14, weight: 10 },
]);
// ─── GIUOCO PIANO (3...Bc5 4.c3) ────────────────────────────────────────────────
addPosition('r1bqk1nr/pppp1ppp/2n5/2b1p3/2B1P3/5N2/PPPP1PPP/RNBQK2R w KQkq - 0 4', [
    { moves: ['c2c3'], minLevel: 7, maxLevel: 20, weight: 40 }, // Modern Italian
    { moves: ['d2d3'], minLevel: 5, maxLevel: 20, weight: 25 }, // Slow Italian
    { moves: ['b1c3'], minLevel: 6, maxLevel: 20, weight: 20 }, // Classical
    { moves: ['e1g1'], minLevel: 5, maxLevel: 20, weight: 10 },
    { moves: ['d2d4'], minLevel: 7, maxLevel: 14, weight: 5 },
]);
// ─── SCOTCH (1.e4 e5 2.Nf3 Nc6 3.d4) ───────────────────────────────────────────
addPosition('r1bqkbnr/pppp1ppp/2n5/4p3/3PP3/5N2/PPP2PPP/RNBQKB1R b KQkq - 0 3', [
    { moves: ['e5d4'], minLevel: 3, maxLevel: 20, weight: 60 }, // Main (forced-ish)
    { moves: ['d7d6'], minLevel: 3, maxLevel: 14, weight: 20 },
    { moves: ['e5e4'], minLevel: 1, maxLevel: 8, weight: 10 },
    { moves: ['f7f6'], minLevel: 1, maxLevel: 6, weight: 5 },
    { moves: ['g8f6'], minLevel: 5, maxLevel: 20, weight: 15 },
]);
// ─── SCOTCH — 3.d4 exd4 4.Nxd4 ─────────────────────────────────────────────────
addPosition('r1bqkbnr/pppp1ppp/2n5/8/3NP3/8/PPP2PPP/RNBQKB1R b KQkq - 0 4', [
    { moves: ['g8f6'], minLevel: 5, maxLevel: 20, weight: 35 }, // Scotch main
    { moves: ['d8h4'], minLevel: 4, maxLevel: 16, weight: 25 }, // Classical check
    { moves: ['f8c5'], minLevel: 5, maxLevel: 20, weight: 25 }, // Classical
    { moves: ['b8c6'], minLevel: 1, maxLevel: 8, weight: 15 },
]);
// ─── KING'S GAMBIT (1.e4 e5 2.f4) ──────────────────────────────────────────────
addPosition('rnbqkbnr/pppp1ppp/8/4p3/4PP2/8/PPPP2PP/RNBQKBNR b KQkq - 0 2', [
    { moves: ['e5f4'], minLevel: 3, maxLevel: 20, weight: 50 }, // Accept
    { moves: ['d7d5'], minLevel: 5, maxLevel: 20, weight: 25 }, // Falkbeer Counter
    { moves: ['f8c5'], minLevel: 5, maxLevel: 20, weight: 15 }, // Classical Counter
    { moves: ['e5e4'], minLevel: 1, maxLevel: 8, weight: 10 },
]);
// ─── SICILIAN DEFENSE (1.e4 c5) ─────────────────────────────────────────────────
addPosition('rnbqkbnr/pp1ppppp/8/2p5/4P3/8/PPPP1PPP/RNBQKBNR w KQkq - 0 2', [
    // Beginners may not know how to play Sicilian
    { moves: ['d2d4'], minLevel: 1, maxLevel: 4, weight: 20 },
    { moves: ['f1c4'], minLevel: 1, maxLevel: 6, weight: 15 }, // Grand Prix style
    { moves: ['b1c3'], minLevel: 1, maxLevel: 6, weight: 15 },
    // Open Sicilian
    { moves: ['g1f3'], minLevel: 4, maxLevel: 20, weight: 60 }, // Best
    { moves: ['f2f4'], minLevel: 6, maxLevel: 16, weight: 10 }, // Grand Prix Attack
]);
// ─── SICILIAN — 2.Nf3 ───────────────────────────────────────────────────────────
addPosition('rnbqkbnr/pp1ppppp/8/2p5/4P3/5N2/PPPP1PPP/RNBQKB1R b KQkq - 0 2', [
    { moves: ['d7d6'], minLevel: 4, maxLevel: 20, weight: 35 }, // Najdorf/Dragon setup
    { moves: ['b8c6'], minLevel: 3, maxLevel: 20, weight: 25 }, // Classical
    { moves: ['e7e6'], minLevel: 4, maxLevel: 20, weight: 20 }, // Scheveningen/Kan
    { moves: ['a7a6'], minLevel: 6, maxLevel: 20, weight: 10 }, // Kan
    { moves: ['g7g6'], minLevel: 6, maxLevel: 20, weight: 10 }, // Hyper-Accelerated
]);
// ─── SICILIAN — 2.Nf3 d6 3.d4 ───────────────────────────────────────────────────
addPosition('rnbqkbnr/pp2pppp/3p4/2p5/4P3/5N2/PPPP1PPP/RNBQKB1R w KQkq - 0 3', [
    { moves: ['d2d4'], minLevel: 4, maxLevel: 20, weight: 80 },
    { moves: ['f1b5'], minLevel: 4, maxLevel: 14, weight: 10 },
    { moves: ['b1c3'], minLevel: 4, maxLevel: 14, weight: 10 },
]);
// ─── SICILIAN — 2.Nf3 d6 3.d4 cxd4 4.Nxd4 ──────────────────────────────────────
addPosition('rnbqkbnr/pp2pppp/3p4/8/3NP3/8/PPP2PPP/RNBQKB1R b KQkq - 0 4', [
    { moves: ['g8f6'], minLevel: 4, maxLevel: 20, weight: 50 }, // Universal Sicilian
    { moves: ['b8c6'], minLevel: 4, maxLevel: 20, weight: 20 }, // Classical
    { moves: ['a7a6'], minLevel: 7, maxLevel: 20, weight: 20 }, // Najdorf
    { moves: ['g7g6'], minLevel: 6, maxLevel: 20, weight: 10 }, // Dragon
]);
// ─── SICILIAN NAJDORF — Nxd4 Nf6 5.Nc3 a6 ──────────────────────────────────────
addPosition('rnbqkb1r/1p2pppp/p2p1n2/8/3NP3/2N5/PPP2PPP/R1BQKB1R w KQkq - 0 6', [
    { moves: ['f1e2'], minLevel: 8, maxLevel: 20, weight: 20 },
    { moves: ['c1g5'], minLevel: 9, maxLevel: 20, weight: 25 }, // English Attack
    { moves: ['f2f3'], minLevel: 10, maxLevel: 20, weight: 20 }, // English Attack
    { moves: ['c1e3'], minLevel: 8, maxLevel: 20, weight: 15 },
    { moves: ['d1d3'], minLevel: 7, maxLevel: 20, weight: 10 },
    { moves: ['f1b5'], minLevel: 9, maxLevel: 20, weight: 10 }, // Adams Attack
]);
// ─── FRENCH DEFENSE (1.e4 e6) ───────────────────────────────────────────────────
addPosition('rnbqkbnr/pppp1ppp/4p3/8/4P3/8/PPPP1PPP/RNBQKBNR w KQkq - 0 2', [
    { moves: ['d2d4'], minLevel: 3, maxLevel: 20, weight: 70 },
    { moves: ['d2d3'], minLevel: 1, maxLevel: 10, weight: 15 }, // King's Indian Attack
    { moves: ['g1f3'], minLevel: 3, maxLevel: 14, weight: 15 },
]);
// ─── FRENCH — 2.d4 d5 ───────────────────────────────────────────────────────────
addPosition('rnbqkbnr/ppp2ppp/4p3/3p4/3PP3/8/PPP2PPP/RNBQKBNR w KQkq - 0 3', [
    { moves: ['b1c3'], minLevel: 5, maxLevel: 20, weight: 30 }, // Winawer/Classical
    { moves: ['b1d2'], minLevel: 5, maxLevel: 20, weight: 25 }, // Tarrasch
    { moves: ['e4e5'], minLevel: 4, maxLevel: 20, weight: 25 }, // Advance
    { moves: ['e4d5'], minLevel: 4, maxLevel: 16, weight: 10 }, // Exchange
    { moves: ['g1f3'], minLevel: 3, maxLevel: 14, weight: 10 },
]);
// ─── CARO-KANN (1.e4 c6) ────────────────────────────────────────────────────────
addPosition('rnbqkbnr/pp1ppppp/2p5/8/4P3/8/PPPP1PPP/RNBQKBNR w KQkq - 0 2', [
    { moves: ['d2d4'], minLevel: 3, maxLevel: 20, weight: 65 },
    { moves: ['b1c3'], minLevel: 3, maxLevel: 14, weight: 15 },
    { moves: ['g1f3'], minLevel: 3, maxLevel: 14, weight: 10 },
    { moves: ['f2f3'], minLevel: 5, maxLevel: 20, weight: 10 }, // Hillbilly Attack
]);
// ─── CARO-KANN — 2.d4 d5 3.e5 ──────────────────────────────────────────────────
addPosition('rnbqkbnr/pp2pppp/2p5/3pP3/3P4/8/PPP2PPP/RNBQKBNR b KQkq - 0 3', [
    { moves: ['c8f5'], minLevel: 5, maxLevel: 20, weight: 50 }, // Main with bishop
    { moves: ['g8f6'], minLevel: 3, maxLevel: 20, weight: 30 },
    { moves: ['c8g4'], minLevel: 5, maxLevel: 16, weight: 10 },
    { moves: ['e7e6'], minLevel: 3, maxLevel: 14, weight: 10 },
]);
// ─── SCANDINAVIAN (1.e4 d5) ─────────────────────────────────────────────────────
addPosition('rnbqkbnr/ppp1pppp/8/3p4/4P3/8/PPPP1PPP/RNBQKBNR w KQkq - 0 2', [
    { moves: ['e4d5'], minLevel: 3, maxLevel: 20, weight: 80 }, // Accept
    { moves: ['e4e5'], minLevel: 1, maxLevel: 8, weight: 10 },
    { moves: ['b1c3'], minLevel: 4, maxLevel: 14, weight: 10 },
]);
// ─── QUEEN'S GAMBIT (1.d4 d5 2.c4) ─────────────────────────────────────────────
addPosition('rnbqkbnr/pppppppp/8/8/2PP4/8/PP2PPPP/RNBQKBNR b KQkq - 0 2', [
    { moves: ['d5c4'], minLevel: 3, maxLevel: 20, weight: 25 }, // QGA
    { moves: ['e7e6'], minLevel: 5, maxLevel: 20, weight: 30 }, // QGD
    { moves: ['c7c6'], minLevel: 5, maxLevel: 20, weight: 25 }, // Slav
    { moves: ['c7c5'], minLevel: 6, maxLevel: 20, weight: 10 }, // Tarrasch
    { moves: ['g8f6'], minLevel: 5, maxLevel: 20, weight: 10 },
]);
// ─── QGD (2...e6 3.Nc3) ──────────────────────────────────────────────────────────
addPosition('rnbqkbnr/ppp2ppp/4p3/3p4/2PP4/8/PP2PPPP/RNBQKBNR w KQkq - 0 3', [
    { moves: ['b1c3'], minLevel: 5, maxLevel: 20, weight: 40 },
    { moves: ['g1f3'], minLevel: 4, maxLevel: 20, weight: 35 },
    { moves: ['c4d5'], minLevel: 3, maxLevel: 14, weight: 15 }, // Exchange QGD
    { moves: ['e2e3'], minLevel: 4, maxLevel: 16, weight: 10 },
]);
// ─── SLAV (1.d4 d5 2.c4 c6) ─────────────────────────────────────────────────────
addPosition('rnbqkbnr/pp2pppp/2p5/3p4/2PP4/8/PP2PPPP/RNBQKBNR w KQkq - 0 3', [
    { moves: ['g1f3'], minLevel: 5, maxLevel: 20, weight: 40 },
    { moves: ['b1c3'], minLevel: 5, maxLevel: 20, weight: 30 },
    { moves: ['e2e3'], minLevel: 4, maxLevel: 16, weight: 15 },
    { moves: ['c4d5'], minLevel: 3, maxLevel: 14, weight: 15 },
]);
// ─── KING'S INDIAN (1.d4 Nf6 2.c4 g6) ──────────────────────────────────────────
addPosition('rnbqkb1r/pppppp1p/5np1/8/2PP4/8/PP2PPPP/RNBQKBNR w KQkq - 0 3', [
    { moves: ['b1c3'], minLevel: 6, maxLevel: 20, weight: 50 }, // Main
    { moves: ['g1f3'], minLevel: 5, maxLevel: 20, weight: 25 },
    { moves: ['e2e4'], minLevel: 7, maxLevel: 20, weight: 20 }, // 4.e4 KID
    { moves: ['g2g3'], minLevel: 6, maxLevel: 20, weight: 5 },
]);
// ─── NIMZO-INDIAN (1.d4 Nf6 2.c4 e6 3.Nc3 Bb4) ─────────────────────────────────
addPosition('rnbqk2r/pppp1ppp/4pn2/8/1bPP4/2N5/PP2PPPP/R1BQKBNR w KQkq - 0 4', [
    { moves: ['d1c2'], minLevel: 9, maxLevel: 20, weight: 30 }, // Classical
    { moves: ['e2e3'], minLevel: 7, maxLevel: 20, weight: 25 }, // Rubinstein
    { moves: ['a2a3'], minLevel: 7, maxLevel: 20, weight: 20 }, // Saemisch
    { moves: ['g1f3'], minLevel: 7, maxLevel: 20, weight: 15 },
    { moves: ['f2f3'], minLevel: 8, maxLevel: 20, weight: 10 }, // Saemisch system
]);
// ─── After 1.d4 Nf6 2.c4 ────────────────────────────────────────────────────────
addPosition('rnbqkb1r/pppppppp/5n2/8/2PP4/8/PP2PPPP/RNBQKBNR b KQkq - 0 2', [
    { moves: ['e7e6'], minLevel: 5, maxLevel: 20, weight: 30 }, // Nimzo / QID
    { moves: ['g7g6'], minLevel: 5, maxLevel: 20, weight: 30 }, // KID / Gruenfeld
    { moves: ['c7c5'], minLevel: 5, maxLevel: 20, weight: 20 }, // Benoni
    { moves: ['d7d5'], minLevel: 4, maxLevel: 20, weight: 10 }, // QGD
    { moves: ['b7b6'], minLevel: 7, maxLevel: 20, weight: 10 }, // QID
]);
// ─── Book Lookup Function ────────────────────────────────────────────────────────
/**
 * Maps a 1-20 difficulty level to an approximate ELO rating.
 */
export function levelToElo(level) {
    const mapping = {
        1: 600, 2: 700, 3: 800, 4: 900,
        5: 1000, 6: 1150, 7: 1300, 8: 1450,
        9: 1600, 10: 1750, 11: 1900, 12: 2050,
        13: 2200, 14: 2350, 15: 2500, 16: 2650,
        17: 2800, 18: 3000, 19: 3200, 20: 3500,
    };
    return mapping[Math.max(1, Math.min(20, Math.round(level)))] ?? 1500;
}
/**
 * Maps a 1-20 level to Stockfish Skill Level (0-20).
 */
export function levelToSkillLevel(level) {
    // Level 1 → Skill 0 ... Level 20 → Skill 20 (Max)
    if (level >= 20)
        return 20;
    // Map 1-19 linearly to 0-19
    return Math.max(0, Math.min(20, Math.floor(level - 1)));
}
/**
 * Maps a 1-20 level to search depth.
 */
export function levelToDepth(level) {
    if (level <= 1)
        return 1;
    if (level <= 3)
        return 2;
    if (level <= 5)
        return 4;
    if (level <= 8)
        return 6;
    if (level <= 10)
        return 10;
    if (level <= 12)
        return 12;
    if (level <= 15)
        return 14;
    if (level <= 18)
        return 16;
    if (level <= 20)
        return 18; // 18 is deep enough for web workers
    return 18;
}
/**
 * Queries the opening book for the current position.
 * Returns a UCI move string or null if not in book.
 *
 * @param fen  The current board FEN
 * @param level  Difficulty level 1-20
 * @param moveNumber  Current move number (book expires at higher moves)
 */
export function getBookMove(fen, level, moveNumber = 0) {
    // Opening book only applies within first ~15 moves
    const bookDepthLimit = Math.max(5, Math.min(15, Math.round(level * 0.75)));
    if (moveNumber > bookDepthLimit)
        return null;
    // Validate move on board
    const chess = new Chess(fen);
    const entries = OPENING_BOOK.get(fenKey(fen));
    if (!entries || entries.length === 0)
        return null;
    // Filter by level
    const applicable = entries.filter(e => {
        const minLvl = e.minLevel ?? 1;
        const maxLvl = e.maxLevel ?? 20;
        return level >= minLvl && level <= maxLvl;
    });
    if (applicable.length === 0)
        return null;
    // Weighted random selection
    const totalWeight = applicable.reduce((sum, e) => sum + e.weight, 0);
    let random = Math.random() * totalWeight;
    for (const entry of applicable) {
        random -= entry.weight;
        if (random <= 0) {
            // Validate the move is legal
            const move = entry.moves[0]; // Use first move in the list
            if (!move)
                continue;
            try {
                const testChess = new Chess(fen);
                const result = testChess.move({
                    from: move.slice(0, 2),
                    to: move.slice(2, 4),
                    promotion: move[4]
                });
                if (result)
                    return move;
            }
            catch {
                continue;
            }
        }
    }
    return null;
}
